import { useState } from "react";
import { toast } from "sonner";
import { LogIn, LogOut, Loader2, MapPinOff } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { fmtClock, fmtMinutes, getPosition, fmtDateTimeMtl } from "@/lib/time";

export const ClockPanel = ({ a, onChange }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const ts = a.timesheet;
  const clock = a.clock;

  const punch = async (kind) => {
    setBusy(true);
    try {
      const pos = await getPosition().catch(() => {
        throw new Error("geo");
      });
      const r = await api.post(`/me/clock-${kind}`, { assignment_id: a.id, ...pos });
      toast.success(kind === "in" ? t("t.clockedIn") : t("t.clockedOut"));
      onChange(a.id, r.data);
    } catch (e) {
      if (e.message === "geo") toast.error(t("t.geoDenied"));
      else {
        const d = e?.response?.data?.detail;
        toast.error(errorText(t, e) + (d?.distance_m ? ` (${d.distance_m} m)` : ""));
      }
    } finally {
      setBusy(false);
    }
  };

  if (ts && ts.status !== "open") {
    return (
      <div className="mt-4 rounded-xl border border-white/[0.07] bg-white/[0.03] px-4 py-3 grid grid-cols-3 text-center text-xs" data-testid={`clock-summary-${a.id}`}>
        <div><div className="eyebrow">{t("t.in")}</div><div className="mono text-sm mt-1">{fmtClock(ts.clock_in)}</div></div>
        <div><div className="eyebrow">{t("t.out")}</div><div className="mono text-sm mt-1">{fmtClock(ts.clock_out)}</div></div>
        <div><div className="eyebrow">{t("t.total")}</div><div className="mono text-sm mt-1 text-[#D9B872]">{fmtMinutes(ts.total_minutes)}</div></div>
      </div>
    );
  }
  if (!clock) return null;
  if (clock.can_clock_out) {
    return (
      <div className="mt-4" data-testid={`clock-panel-${a.id}`}>
        <p className="text-xs text-emerald-700 mb-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{t("t.onShift")} {fmtClock(ts.clock_in)}</p>
        <button data-testid={`clock-out-btn-${a.id}`} disabled={busy} onClick={() => punch("out")} className="press w-full h-14 rounded-full border border-[#C5A059] text-[#D9B872] font-semibold flex items-center justify-center gap-2 hover:bg-[#C5A059]/10 disabled:opacity-50">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}{busy ? t("t.locating") : t("t.clockOut")}
        </button>
      </div>
    );
  }
  if (clock.can_clock_in) {
    return (
      <div className="mt-4" data-testid={`clock-panel-${a.id}`}>
        {!clock.has_location && <p className="text-xs text-red-600 mb-2 flex items-center gap-2"><MapPinOff className="w-3.5 h-3.5" />{t("err.no_event_location")}</p>}
        <button data-testid={`clock-in-btn-${a.id}`} disabled={busy} onClick={() => punch("in")} className="press w-full h-14 rounded-full bg-[#C5A059] text-[#0A0A0C] font-semibold flex items-center justify-center gap-2 hover:bg-[#D9B872] disabled:opacity-50">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}{busy ? t("t.locating") : t("t.clockIn")}
        </button>
      </div>
    );
  }
  return (
    <p className="mt-4 text-[11px] text-neutral-500" data-testid={`clock-opens-${a.id}`}>
      {t("t.opensAt")} <span className="mono">{fmtDateTimeMtl(clock.opens_at)}</span>
    </p>
  );
};
